<template>
  <div class="card mb-4">
    <div class="card-header pb-0">
      <h6>Produtos</h6>
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <p class="text-sm mb-0">
          <span class="font-weight-bold">{{ total }}</span> produtos encontrados
        </p>
        <div class="d-flex gap-2 align-items-center flex-wrap">
          <div class="search-group">
            <i class="fas fa-search search-icon"></i>
            <input
              type="text"
              class="search-input"
              placeholder="Buscar produto... (mín. 3 letras)"
              v-model="searchQuery"
              @input="onSearchInput"
            >
            <button
              v-if="searchQuery"
              class="search-clear"
              type="button"
              @click="clearSearch"
              title="Limpar busca"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <button
            @click="loadProducts"
            class="btn btn-sm mb-0"
            :class="loading ? 'btn-secondary' : 'btn-custom-gradient'"
            :disabled="loading"
          >
            {{ loading ? 'Carregando...' : 'Atualizar' }}
          </button>
        </div>
      </div>
    </div>
    <div class="card-body">
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border spinner-teal" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
      </div>
      <div v-else>
        <div v-if="products.length === 0" class="text-center py-4">
          <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
          <p class="text-muted">Nenhum produto encontrado</p>
        </div>
        <div v-else class="products-grid">
          <div
            v-for="(product, idx) in products"
            :key="product.id_product ?? product.id ?? idx"
            class="product-card"
          >
            <div
              class="product-thumb"
              :class="{ 'product-thumb--clickable': getImageUrl(product) }"
              @click="getImageUrl(product) && openImageLightbox(getImageUrl(product))"
              title="Ver imagem ampliada"
            >
              <img
                v-if="getImageUrl(product)"
                :src="getImageUrl(product)"
                :alt="product.name"
                @error="$event.target.style.display = 'none'"
              />
              <i v-else class="fas fa-box-open product-no-img"></i>
              <button
                class="product-edit-image-btn"
                type="button"
                title="Editar imagem"
                @click.stop="openEditImageModal(product)"
              >
                <i class="fas fa-pencil-alt"></i>
              </button>
              <button
                class="product-deactivate-btn"
                type="button"
                title="Desativar produto"
                :disabled="deactivatingIds.has(getProductId(product))"
                @click.stop="deactivateProduct(product)"
              >
                <i class="fas" :class="deactivatingIds.has(getProductId(product)) ? 'fa-circle-notch fa-spin' : 'fa-power-off'"></i>
              </button>
            </div>
            <div class="product-info">
              <p class="product-name">{{ product.name || 'Sem nome' }}</p>
              <div class="product-meta">
                <span>{{ product.quantity }} {{ product.unit }}</span>
                <button
                  class="product-edit-data-btn"
                  type="button"
                  title="Editar produto"
                  @click="openEditProductModal(product)"
                >
                  <i class="fas fa-edit"></i>
                  Editar
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Paginação -->
        <div v-if="lastPage > 1" class="products-pagination">
          <span class="pagination-info">
            Página {{ currentPage }} de {{ lastPage }} — {{ total }} produto(s)
          </span>
          <nav>
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="javascript:;" @click="goToPage(currentPage - 1)">
                  <i class="fas fa-chevron-left"></i>
                </a>
              </li>
              <li
                v-for="p in visiblePages"
                :key="p"
                class="page-item"
                :class="{ active: currentPage === p }"
              >
                <a class="page-link" href="javascript:;" @click="goToPage(p)">{{ p }}</a>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === lastPage }">
                <a class="page-link" href="javascript:;" @click="goToPage(currentPage + 1)">
                  <i class="fas fa-chevron-right"></i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Lightbox de imagem ampliada -->
    <div v-if="showImageLightbox" class="modal-backdrop fade show" @click="closeImageLightbox" style="z-index: 10999;"></div>
    <div v-if="showImageLightbox" class="image-lightbox" @click="closeImageLightbox" style="z-index: 11000;">
      <button type="button" class="image-lightbox-close" @click.stop="closeImageLightbox" aria-label="Close">
        <i class="fas fa-times"></i>
      </button>
      <img :src="lightboxImageUrl" alt="Produto ampliado" class="image-lightbox-img" @click.stop />
    </div>

    <!-- Modal de Editar Imagem do Produto -->
    <div v-if="showEditImageModal" class="modal-backdrop fade show" @click="closeEditImageModal" style="z-index: 9999;"></div>
    <div v-if="showEditImageModal" class="modal fade show d-block" tabindex="-1" aria-labelledby="editProductImageModalLabel" aria-hidden="true" style="z-index: 10000;">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editProductImageModalLabel">
              <i class="fas fa-image me-2"></i>
              Editar Imagem - {{ editingProduct?.name || '' }}
            </h5>
            <button type="button" class="btn-close" @click="closeEditImageModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div
              class="edit-image-upload-zone"
              :class="{ 'has-file': editImageFile }"
              @click="triggerEditImageInput"
              @dragover.prevent="editImageDragging = true"
              @dragleave.prevent="editImageDragging = false"
              @drop.prevent="handleEditImageDrop"
              :style="editImageDragging ? 'border-color:#17c1e8;' : ''"
            >
              <template v-if="editImagePreviewUrl">
                <img :src="editImagePreviewUrl" alt="Preview" class="edit-image-preview" />
              </template>
              <template v-else>
                <div class="upload-placeholder">
                  <i class="fas fa-cloud-upload-alt mb-2"></i>
                  <p class="mb-0 text-sm">Arraste ou clique para selecionar a nova imagem</p>
                </div>
              </template>
            </div>
            <input ref="editImageInput" type="file" accept="image/*" class="d-none" @change="handleEditImageFileChange" />
            <div v-if="editImageError" class="alert alert-danger py-2 mt-3 mb-0">
              {{ editImageError }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeEditImageModal" :disabled="savingImage">Cancelar</button>
            <button
              type="button"
              class="btn btn-custom-gradient"
              @click="saveProductImage"
              :disabled="!editImageFile || savingImage"
            >
              <span v-if="savingImage">
                <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                Enviando...
              </span>
              <span v-else>
                <i class="fas fa-save me-1"></i>
                Salvar
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Editar Produto -->
    <div v-if="showEditProductModal" class="modal-backdrop fade show" @click="closeEditProductModal" style="z-index: 9999;"></div>
    <div v-if="showEditProductModal" class="modal fade show d-block" tabindex="-1" aria-labelledby="editProductModalLabel" aria-hidden="true" style="z-index: 10000;">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editProductModalLabel">
              <i class="fas fa-edit me-2"></i>
              Editar Produto
            </h5>
            <button type="button" class="btn-close" @click="closeEditProductModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveProductData">
              <div class="mb-3">
                <label class="form-label">Nome *</label>
                <input type="text" class="form-control" v-model="editProductForm.name" placeholder="Nome do produto">
              </div>
              <div class="row">
                <div class="col-6 mb-3">
                  <label class="form-label">Quantidade</label>
                  <input type="text" class="form-control" v-model="editProductForm.quantity" placeholder="Ex: 1.000">
                </div>
                <div class="col-6 mb-3">
                  <label class="form-label">Unidade</label>
                  <input type="text" class="form-control" v-model="editProductForm.unit" placeholder="Ex: kg">
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label">Marca</label>
                <input type="text" class="form-control" v-model="editProductForm.brand" placeholder="Ex: Friboi">
              </div>
            </form>
            <div v-if="editProductError" class="alert alert-danger py-2 mb-0">
              {{ editProductError }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeEditProductModal" :disabled="savingProductData">Cancelar</button>
            <button
              type="button"
              class="btn btn-custom-gradient"
              @click="saveProductData"
              :disabled="!editProductForm.name || savingProductData"
            >
              <span v-if="savingProductData">
                <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                Salvando...
              </span>
              <span v-else>
                <i class="fas fa-save me-1"></i>
                Salvar
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_ENDPOINTS, apiRequest, handleApiResponse, STORAGE_BASE_URL } from "@/config/api.js";
import { showSuccessAlert, showErrorAlert, showConfirmAlert } from "@/utils/swal.js";

export default {
  name: "produtos-catalogo-cards",
  data() {
    return {
      products: [],
      loading: false,
      error: null,
      currentPage: 1,
      lastPage: 1,
      total: 0,
      perPage: 15,
      searchQuery: "",
      searchDebounce: null,
      showEditImageModal: false,
      editingProduct: null,
      editImageFile: null,
      editImagePreviewUrl: null,
      editImageDragging: false,
      savingImage: false,
      editImageError: null,
      deactivatingIds: new Set(),
      showEditProductModal: false,
      editingProductData: null,
      editProductForm: {
        name: "",
        quantity: "",
        unit: "",
        brand: "",
      },
      savingProductData: false,
      editProductError: null,
      showImageLightbox: false,
      lightboxImageUrl: null,
    };
  },
  computed: {
    visiblePages() {
      const total = this.lastPage;
      const current = this.currentPage;
      const delta = 2;
      const pages = [];
      for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
        pages.push(i);
      }
      return pages;
    },
  },
  mounted() {
    this.loadProducts();
  },
  methods: {
    async loadProducts() {
      this.loading = true;
      this.error = null;

      try {
        const params = new URLSearchParams({
          per_page: this.perPage,
          page: this.currentPage,
        });

        const query = this.searchQuery.trim();
        if (query.length >= 3) {
          params.set("description", query);
        }

        const response = await apiRequest(
          `${API_ENDPOINTS.FLYER_PRODUCTS_CATALOG}?${params.toString()}`,
          { method: "GET" }
        );

        const data = await handleApiResponse(response);
        this.products = Array.isArray(data) ? data : (data.data || []);
        this.currentPage = data.current_page || this.currentPage;
        this.lastPage = data.last_page || 1;
        this.total = data.total ?? this.products.length;
      } catch (error) {
        console.error("Erro ao carregar produtos:", error);
        this.error = error.message || "Erro ao carregar produtos";
      } finally {
        this.loading = false;
      }
    },

    goToPage(page) {
      if (page < 1 || page > this.lastPage || page === this.currentPage) return;
      this.currentPage = page;
      this.loadProducts();
    },

    onSearchInput() {
      clearTimeout(this.searchDebounce);
      const query = this.searchQuery.trim();

      if (query === "") {
        this.clearSearch();
        return;
      }

      if (query.length < 3) return;

      this.searchDebounce = setTimeout(() => {
        this.currentPage = 1;
        this.loadProducts();
      }, 400);
    },

    clearSearch() {
      clearTimeout(this.searchDebounce);
      this.searchQuery = "";
      this.currentPage = 1;
      this.loadProducts();
    },

    getImageUrl(product) {
      const path = product.image_path || product.image || product.image_url;
      if (!path) return null;
      if (/^https?:\/\//i.test(path)) return path;
      return `${STORAGE_BASE_URL}/${String(path).replace(/^\/+/, "")}`;
    },

    openImageLightbox(url) {
      this.lightboxImageUrl = url;
      this.showImageLightbox = true;
    },

    closeImageLightbox() {
      this.showImageLightbox = false;
      this.lightboxImageUrl = null;
    },

    openEditImageModal(product) {
      this.editingProduct = product;
      this.editImageFile = null;
      this.editImagePreviewUrl = null;
      this.editImageError = null;
      this.showEditImageModal = true;
    },

    closeEditImageModal() {
      if (this.savingImage) return;
      this.showEditImageModal = false;
      this.editingProduct = null;
      if (this.editImagePreviewUrl) URL.revokeObjectURL(this.editImagePreviewUrl);
      this.editImagePreviewUrl = null;
      this.editImageFile = null;
      this.editImageError = null;
    },

    triggerEditImageInput() {
      this.$refs.editImageInput.click();
    },

    handleEditImageFileChange(event) {
      const file = event.target.files[0];
      if (file) this.setEditImageFile(file);
    },

    handleEditImageDrop(event) {
      this.editImageDragging = false;
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith("image/")) this.setEditImageFile(file);
    },

    setEditImageFile(file) {
      if (this.editImagePreviewUrl) URL.revokeObjectURL(this.editImagePreviewUrl);
      this.editImageFile = file;
      this.editImagePreviewUrl = URL.createObjectURL(file);
      this.editImageError = null;
    },

    async saveProductImage() {
      if (!this.editImageFile || !this.editingProduct) return;

      this.savingImage = true;
      this.editImageError = null;

      try {
        const formData = new FormData();
        formData.append("image", this.editImageFile);
        formData.append("_method", "PUT");

        const response = await apiRequest(
          API_ENDPOINTS.FLYER_PRODUCT_IMAGE(this.getProductId(this.editingProduct)),
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await handleApiResponse(response);

        const newPath = data?.image_path || data?.image || data?.image_url;
        if (newPath) {
          this.editingProduct.image_path = newPath;
        }

        this.closeEditImageModal();
        await showSuccessAlert("Imagem atualizada!", "A imagem do produto foi atualizada com sucesso.", 3000);
      } catch (error) {
        console.error("Erro ao atualizar imagem do produto:", error);
        this.editImageError = error.message || "Erro ao atualizar imagem do produto";
        showErrorAlert("Erro ao atualizar imagem", this.editImageError);
      } finally {
        this.savingImage = false;
      }
    },

    getProductId(product) {
      return product.id_product ?? product.id;
    },

    openEditProductModal(product) {
      this.editingProductData = product;
      this.editProductForm = {
        name: product.name || "",
        quantity: product.quantity || "",
        unit: product.unit || "",
        brand: product.brand || "",
      };
      this.editProductError = null;
      this.showEditProductModal = true;
    },

    closeEditProductModal() {
      if (this.savingProductData) return;
      this.showEditProductModal = false;
      this.editingProductData = null;
      this.editProductError = null;
    },

    async saveProductData() {
      if (!this.editingProductData || !this.editProductForm.name) return;

      this.savingProductData = true;
      this.editProductError = null;

      try {
        const response = await apiRequest(
          API_ENDPOINTS.FLYER_PRODUCT_UPDATE(this.getProductId(this.editingProductData)),
          {
            method: "PUT",
            body: JSON.stringify({
              name: this.editProductForm.name,
              quantity: this.editProductForm.quantity,
              unit: this.editProductForm.unit,
              brand: this.editProductForm.brand,
            }),
          }
        );

        const data = await handleApiResponse(response);

        Object.assign(this.editingProductData, {
          name: data?.name ?? this.editProductForm.name,
          quantity: data?.quantity ?? this.editProductForm.quantity,
          unit: data?.unit ?? this.editProductForm.unit,
          brand: data?.brand ?? this.editProductForm.brand,
        });

        this.showEditProductModal = false;
        this.editingProductData = null;
        await showSuccessAlert("Produto atualizado!", "Os dados do produto foram atualizados com sucesso.", 3000);
      } catch (error) {
        console.error("Erro ao atualizar produto:", error);
        this.editProductError = error.message || "Erro ao atualizar produto";
      } finally {
        this.savingProductData = false;
      }
    },

    async deactivateProduct(product) {
      const id = this.getProductId(product);

      const result = await showConfirmAlert(
        "Desativar Produto",
        `Tem certeza que deseja desativar "${product.name || 'este produto'}"?`,
        "Sim, desativar",
        "Cancelar"
      );
      if (!result.isConfirmed) return;

      this.deactivatingIds.add(id);

      try {
        await handleApiResponse(
          await apiRequest(API_ENDPOINTS.FLYER_PRODUCT_DEACTIVATE(id), {
            method: "PATCH",
          })
        );

        this.products = this.products.filter((p) => this.getProductId(p) !== id);
        this.total = Math.max(0, this.total - 1);
        await showSuccessAlert("Produto desativado!", "O produto foi desativado com sucesso.", 3000);
      } catch (error) {
        console.error("Erro ao desativar produto:", error);
        this.showDeactivateBlockedError(error);
      } finally {
        this.deactivatingIds.delete(id);
      }
    },

    showDeactivateBlockedError(error) {
      const locations = this.extractPriceLocations(error.data);

      if (locations.length === 0) {
        showErrorAlert("Não foi possível desativar", error.message || "Erro ao desativar produto");
        return;
      }

      const lines = locations
        .map((loc) => `${loc.market}${loc.price ? ` — R$ ${loc.price}` : ""}`)
        .join("\n");

      showErrorAlert(
        "Produto possui preço cadastrado",
        `Este produto não pode ser desativado pois tem preço nos seguintes mercados:\n\n${lines}`
      );
    },

    extractPriceLocations(data) {
      if (!data || typeof data !== "object") return [];

      const list =
        data.active_prices ||
        data.data?.active_prices ||
        data.prices ||
        data.data?.prices ||
        data.markets ||
        data.supermarkets ||
        data.data ||
        [];

      if (!Array.isArray(list)) return [];

      return list
        .map((item) => {
          const market =
            item.supermarket?.name ||
            item.supermarket?.nome ||
            (typeof item.supermarket === "string" ? item.supermarket : null) ||
            item.market?.name ||
            item.market?.nome ||
            item.market_name ||
            item.supermarket_name ||
            item.name ||
            item.nome ||
            null;
          if (!market) return null;
          return { market, price: item.price ?? item.valor ?? null };
        })
        .filter(Boolean);
    },
  },
};
</script>

<style scoped>
.btn-custom-gradient {
  background: linear-gradient(310deg, #17c1e8 0%, #20c997 100%);
  border: none;
  color: white;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-custom-gradient:hover {
  background: linear-gradient(310deg, #15a8d1 0%, #1bb88a 100%);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(23, 193, 232, 0.3);
}

.spinner-teal {
  color: #20c997 !important;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
}

.product-card {
  display: flex;
  flex-direction: column;
  border: 1px solid #eef0f4;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.product-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.product-thumb {
  position: relative;
  height: 110px;
  background: linear-gradient(135deg, #f5f7fa 0%, #eef0f4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.product-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-edit-image-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
  z-index: 2;
}

.product-edit-image-btn:hover {
  background: #17c1e8;
  transform: scale(1.1);
}

.product-deactivate-btn {
  position: absolute;
  top: 6px;
  left: 6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
  z-index: 2;
}

.product-deactivate-btn:hover:not(:disabled) {
  background: #ea0606;
  transform: scale(1.1);
}

.product-deactivate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.edit-image-upload-zone {
  border: 2px dashed #dee2e6;
  border-radius: 0.75rem;
  padding: 1.5rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}

.edit-image-upload-zone:hover {
  border-color: #17c1e8;
  background: rgba(23, 193, 232, 0.04);
}

.edit-image-upload-zone.has-file {
  border-color: #20c997;
  border-style: solid;
  background: rgba(32, 201, 151, 0.04);
  padding: 0.5rem;
}

.edit-image-upload-zone .upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #6c757d;
}

.edit-image-upload-zone .upload-placeholder i {
  font-size: 1.8rem;
  color: #17c1e8;
}

.edit-image-preview {
  max-width: 100%;
  max-height: 220px;
  object-fit: contain;
  border-radius: 8px;
}

.product-thumb--clickable {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.product-thumb--clickable:hover {
  opacity: 0.8;
}

.product-no-img {
  font-size: 2rem;
  color: #ced4da;
}

.product-info {
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.product-name {
  font-size: 0.8rem;
  font-weight: 700;
  color: #344767;
  margin: 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 2.7em;
}

.product-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.7rem;
  color: #8392a5;
}

.product-edit-data-btn {
  border: none;
  background: transparent;
  color: #17c1e8;
  font-weight: 700;
  font-size: 0.68rem;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s ease;
}

.product-edit-data-btn:hover {
  color: #0ea5c7;
}


.products-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 4px 0;
  border-top: 1px solid #f0f0f0;
  margin-top: 16px;
}

.pagination-info {
  font-size: 0.78rem;
  color: #8392a5;
}

.page-item.active .page-link {
  background: linear-gradient(310deg, #17c1e8 0%, #20c997 100%);
  border-color: transparent;
  color: #fff;
}

.page-link {
  color: #17c1e8;
  border-color: #e9ecef;
  font-size: 0.8rem;
}

.page-link:hover {
  color: #17c1e8;
  background-color: rgba(23, 193, 232, 0.08);
  border-color: #e9ecef;
}

.modal-backdrop {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
}

.image-lightbox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
}

.image-lightbox-img {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  cursor: default;
}

.image-lightbox-close {
  position: absolute;
  top: 24px;
  right: 32px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
}

.image-lightbox-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.search-group {
  position: relative;
  width: 100%;
  max-width: 260px;
  flex-shrink: 1;
}

.search-group .search-icon {
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  font-size: 0.8rem;
  color: #adb5bd;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 38px;
  padding: 0 38px 0 36px;
  font-size: 0.875rem;
  color: #495057;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 20px;
  outline: none;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.search-input::placeholder {
  color: #adb5bd;
}

.search-input:focus {
  background-color: #fff;
  border-color: #20c997;
  box-shadow: 0 0 0 3px rgba(32, 201, 151, 0.15);
}

.search-clear {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #adb5bd;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.search-clear:hover {
  background-color: #e9ecef;
  color: #495057;
}
</style>
